import { useMemo, useState, type FormEvent } from 'react';
import {
  clientsApi,
  rateCardsApi,
  zonesApi,
  peopleApi,
  type Client,
  type ClientWrite,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { useConfirm, useToast } from '@/components/Feedback';
import { formatPeso } from '@/lib/format';
import { billingPeriodLabel } from '@/lib/labels';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';

type ClientForm = ClientWrite;

const EMPTY_FORM: ClientForm = {
  legalName: '',
  tradeName: '',
  tin: '',
  billingEmail: '',
  billingAddress: '',
  tradingAddress: '',
  paymentTerms: 'Net 30',
  billingPeriod: 'monthly',
  rateCardId: '',
  creditLimit: '0',
  scanningRequired: false,
  allowedZones: [],
  houseAccount: false,
  portalEnabled: true,
  accountManagerId: '',
  contactPerson: '',
  contactEmail: '',
  contactPhone: '',
};

function personLabel(p: { fullName?: string; name?: string }) {
  return p.fullName ?? p.name ?? 'Unknown';
}

export function AdminClients() {
  const { data: clients, loading, error: loadError, reload } = useAsync(() => clientsApi.list(), []);
  const { data: rateCards } = useAsync(() => rateCardsApi.list(), []);
  const { data: zones } = useAsync(() => zonesApi.list(), []);
  const { data: salesPeople } = useAsync(() => peopleApi.list({ isCrew: false }), []);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [form, setForm] = useState<ClientForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const { flash, ok: showOk, error: showError, warn: showWarn, clear: clearFlash } = useFlash();
  const confirm = useConfirm();
  const toast = useToast();
  const [error, setError] = useState('');

  const [inviteTarget, setInviteTarget] = useState<Client | null>(null);
  const [inviteForm, setInviteForm] = useState({ email: '', password: '', clientRole: 'Client Main' });
  const [inviteSaving, setInviteSaving] = useState(false);
  const { flash: inviteFlash, ok: inviteOk, error: inviteError, warn: inviteWarn, clear: inviteClear } = useFlash();

  const salesOwners = useMemo(
    () => (salesPeople ?? []).filter((p) => p.isSales),
    [salesPeople],
  );

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: Client) => {
    setEditing(row);
    setForm({
      legalName: row.legalName,
      tradeName: row.tradeName ?? '',
      tin: row.tin ?? '',
      billingEmail: row.billingEmail ?? '',
      billingAddress: row.billingAddress ?? '',
      tradingAddress: row.tradingAddress ?? '',
      paymentTerms: row.paymentTerms ?? 'Net 30',
      billingPeriod: row.billingPeriod ?? 'monthly',
      rateCardId: row.rateCardId ?? '',
      creditLimit: row.creditLimit ?? '0',
      scanningRequired: row.scanningRequired ?? false,
      allowedZones: row.allowedZones ?? [],
      houseAccount: row.houseAccount ?? false,
      portalEnabled: row.portalEnabled ?? true,
      accountManagerId: row.accountManagerId ?? '',
      contactPerson: row.contactPerson ?? '',
      contactEmail: row.contactEmail ?? '',
      contactPhone: row.contactPhone ?? '',
    });
    setError('');
    setShowForm(true);
  };

  const buildPayload = (): ClientWrite => ({
    legalName: form.legalName.trim(),
    tradeName: form.tradeName || undefined,
    tin: form.tin || undefined,
    billingEmail: form.billingEmail || undefined,
    billingAddress: form.billingAddress || undefined,
    tradingAddress: form.tradingAddress || undefined,
    paymentTerms: form.paymentTerms || undefined,
    billingPeriod: form.billingPeriod,
    rateCardId: form.rateCardId || null,
    creditLimit: form.creditLimit || '0',
    scanningRequired: form.scanningRequired ?? false,
    allowedZones: form.allowedZones ?? [],
    houseAccount: form.houseAccount ?? false,
    portalEnabled: form.portalEnabled ?? true,
    accountManagerId: form.accountManagerId || null,
    contactPerson: form.contactPerson || undefined,
    contactEmail: form.contactEmail || undefined,
    contactPhone: form.contactPhone || undefined,
  });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      if (editing) {
        await clientsApi.update(editing.id, payload);
        toast(`${payload.legalName} updated`);
        clearFlash();
      } else {
        const created = await clientsApi.create(payload);
        toast(`${payload.legalName} added`);
        if (created?.status === 'active') {
          showOk(`${payload.legalName} added and active — trips can be booked for them now.`);
        } else {
          showWarn(
            <>
              {payload.legalName} was added but is not active yet, so trips can't be booked for them.{' '}
              <Button
                type="button"
                size="sm"
                onClick={() => void setStatus(created, 'active')}
              >
                Activate now
              </Button>
            </>,
          );
        }
      }
      setShowForm(false);
      reload();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const toggleZone = (zoneId: string) => {
    const current = form.allowedZones ?? [];
    setForm({
      ...form,
      allowedZones: current.includes(zoneId)
        ? current.filter((z) => z !== zoneId)
        : [...current, zoneId],
    });
  };

  const setStatus = async (row: Client, next: 'active' | 'suspended') => {
    if (next === 'suspended') {
      const confirmed = await confirm({
        title: `Deactivate ${row.legalName}?`,
        message:
          'No new trips can be booked for this client, by your team or through their client portal. ' +
          'Trips already booked are not affected, and you can activate the client again at any time.',
        confirmLabel: 'Deactivate client',
        danger: true,
      });
      if (!confirmed) return;
    }
    setSaving(true);
    clearFlash();
    try {
      if (next === 'active') await clientsApi.activate(row.id);
      else await clientsApi.deactivate(row.id);
      const text = next === 'active' ? `${row.legalName} activated — trips can now be booked` : `${row.legalName} deactivated`;
      showOk(text);
      toast(text);
      reload();
    } catch (err) {
      showError(errorText(err, `Could not ${next === 'active' ? 'activate' : 'deactivate'} ${row.legalName}.`));
    } finally {
      setSaving(false);
    }
  };

  const openInvite = (row: Client) => {
    setInviteTarget(row);
    setInviteForm({ email: row.contactEmail ?? '', password: '', clientRole: 'Client Main' });
    inviteClear();
  };

  const sendInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!inviteTarget) return;
    setInviteSaving(true);
    inviteClear();
    try {
      const result = await clientsApi.inviteUser(inviteTarget.id, inviteForm);
      const delivery = result.emailDelivery;
      if (delivery?.mode === 'live') {
        inviteOk(`Invited ${inviteForm.email} as ${inviteForm.clientRole} — email sent`);
      } else if (delivery?.mode === 'error') {
        inviteWarn(
          `Login created for ${inviteForm.email}, but the invitation email could not be sent (${delivery.message ?? 'unknown error'}). Share the password with them directly.`,
        );
      } else {
        inviteOk(
          `Login created for ${inviteForm.email}. No email was sent because email isn't set up yet (Integrations → Resend), so share the password with them directly.`,
        );
      }
    } catch (err) {
      inviteError(errorText(err, 'Could not invite the user.'));
    } finally {
      setInviteSaving(false);
    }
  };

  const columns = [
    {
      key: 'legalName',
      header: 'Legal name',
      value: (row: Client) =>
        row.tradeName ? `${row.legalName} ${row.tradeName}` : row.legalName,
      render: (row: Client) => (
        <div>
          <div>{row.legalName}</div>
          {row.tradeName && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{row.tradeName}</div>}
        </div>
      ),
    },
    {
      key: 'tin',
      header: 'TIN',
      value: (row: Client) => row.tin ?? '',
      render: (row: Client) => row.tin ?? '—',
    },
    {
      key: 'billing',
      header: 'Billing',
      value: (row: Client) =>
        `${billingPeriodLabel(row.billingPeriod ?? 'monthly')} · ${row.paymentTerms ?? 'Net 30'}`,
      render: (row: Client) => (
        <span>
          {billingPeriodLabel(row.billingPeriod ?? 'monthly')} · {row.paymentTerms ?? 'Net 30'}
        </span>
      ),
    },
    {
      key: 'credit',
      header: 'Credit limit',
      value: (row: Client) => (row.creditLimit ? Number(row.creditLimit) : null),
      render: (row: Client) =>
        row.creditLimit ? formatPeso(row.creditLimit) : '—',
    },
    {
      key: 'portal',
      header: 'Portal',
      value: (row: Client) => (row.portalEnabled ? 'Enabled' : 'Disabled'),
      render: (row: Client) => (row.portalEnabled ? 'Enabled' : 'Disabled'),
    },
    {
      key: 'status',
      header: 'Status',
      value: (row: Client) => row.status,
      render: (row: Client) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: Client) => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>Edit</Button>
          {row.status === 'active' ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => void setStatus(row, 'suspended')}>Deactivate</Button>
          ) : (
            <Button type="button" size="sm" variant="secondary" loading={saving} onClick={() => void setStatus(row, 'active')}>Activate</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => openInvite(row)}>Invite user</Button>
        </div>
      ),
    },
  ];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <p className="kicker">Clients</p>
            <h1>Clients</h1>
            <p className="text-muted">
              Commercial terms, credit, allowed zones, portal access, and sales ownership
            </p>
          </div>
          <Button onClick={openAdd}>Add client</Button>
        </div>

        <FlashMessage flash={flash} />

        <DataTable
          columns={columns}
          data={clients ?? []}
          keyField="id"
          loading={loading}
          loadError={loadError}
          dense
          searchPlaceholder="Filter clients…"
          emptyMessage="No clients yet. Add clients to enable bookings and portal access."
        />

        <Modal
          open={showForm}
          onClose={() => setShowForm(false)}
          title={editing ? 'Edit client' : 'Add client'}
          size="lg"
        >
          <form onSubmit={save} className="client-form">
            <div className="grid-2">
              <Input
                label="Legal name"
                value={form.legalName}
                onChange={(e) => setForm({ ...form, legalName: e.target.value })}
                required
                placeholder="Acme Trading Corp."
              />
              <Input
                label="Trade name"
                value={form.tradeName ?? ''}
                onChange={(e) => setForm({ ...form, tradeName: e.target.value })}
                placeholder="Acme Trading"
              />
            </div>

            <div className="grid-2">
              <Input
                label="TIN"
                value={form.tin ?? ''}
                onChange={(e) => setForm({ ...form, tin: e.target.value })}
                placeholder="000-000-000-000"
              />
              <Input
                label="Billing email"
                type="email"
                value={form.billingEmail ?? ''}
                onChange={(e) => setForm({ ...form, billingEmail: e.target.value })}
                placeholder="billing@client.com"
              />
            </div>

            <Input
              label="Billing address"
              value={form.billingAddress ?? ''}
              onChange={(e) => setForm({ ...form, billingAddress: e.target.value })}
            />
            <Input
              label="Trading / warehouse address"
              value={form.tradingAddress ?? ''}
              onChange={(e) => setForm({ ...form, tradingAddress: e.target.value })}
            />

            <div className="grid-2">
              <Input
                label="Payment terms"
                value={form.paymentTerms ?? ''}
                onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })}
                placeholder="Net 30"
              />
              <Select
                label="Billing period"
                value={form.billingPeriod ?? 'monthly'}
                onChange={(e) =>
                  setForm({ ...form, billingPeriod: e.target.value as ClientForm['billingPeriod'] })
                }
              >
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </Select>
            </div>

            <div className="grid-2">
              <Input
                label="Credit limit (₱)"
                type="number"
                step="0.01"
                min="0"
                value={form.creditLimit ?? '0'}
                onChange={(e) => setForm({ ...form, creditLimit: e.target.value })}
              />
              <Select
                label="Rate card"
                value={form.rateCardId ?? ''}
                onChange={(e) => setForm({ ...form, rateCardId: e.target.value })}
                hint="Determines how trips for this client are priced"
              >
                <option value="">None yet</option>
                {(rateCards ?? []).map((rc) => (
                  <option key={rc.id} value={rc.id}>{rc.name} ({rc.status})</option>
                ))}
              </Select>
            </div>

            <div className="grid-2">
              <Select
                label="House account"
                value={form.houseAccount ? 'yes' : 'no'}
                onChange={(e) => setForm({ ...form, houseAccount: e.target.value === 'yes' })}
                hint="House accounts skip credit-limit blocking"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </Select>
              <Select
                label="Scanning required"
                value={form.scanningRequired ? 'yes' : 'no'}
                onChange={(e) => setForm({ ...form, scanningRequired: e.target.value === 'yes' })}
              >
                <option value="no">No</option>
                <option value="yes">Yes — piece-scanning enforced</option>
              </Select>
            </div>

            <div className="grid-2">
              <Select
                label="Sales owner"
                value={form.accountManagerId ?? ''}
                onChange={(e) => setForm({ ...form, accountManagerId: e.target.value })}
                hint="Commission owner — must be an active sales agent; syncs to the Sales dashboard"
              >
                <option value="">Unassigned</option>
                {salesOwners.map((p) => (
                  <option key={p.id} value={p.id}>{personLabel(p)}</option>
                ))}
              </Select>
              <Select
                label="Portal access"
                value={form.portalEnabled ? 'yes' : 'no'}
                onChange={(e) => setForm({ ...form, portalEnabled: e.target.value === 'yes' })}
              >
                <option value="yes">Enabled</option>
                <option value="no">Disabled</option>
              </Select>
            </div>

            <div className="zones-block">
              <label className="input-label">Allowed zones</label>
              <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: 0 }}>
                Trips can only be booked between these zones. Leave empty to allow all.
              </p>
              <div className="zone-checks">
                {(zones ?? []).map((z) => (
                  <label key={z.id} className="zone-check">
                    <input
                      type="checkbox"
                      checked={(form.allowedZones ?? []).includes(z.id)}
                      onChange={() => toggleZone(z.id)}
                    />
                    {z.code} · {z.name}
                  </label>
                ))}
                {!zones?.length && <span className="text-muted">No zones configured yet</span>}
              </div>
            </div>

            <div className="grid-2">
              <Input
                label="Contact person"
                value={form.contactPerson ?? ''}
                onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
              />
              <Input
                label="Contact phone"
                value={form.contactPhone ?? ''}
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
              />
            </div>
            <Input
              label="Contact email"
              type="email"
              value={form.contactEmail ?? ''}
              onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
            />

            {error && <p className="text-urgent">{error}</p>}
            <Button type="submit" loading={saving}>
              {editing ? 'Save changes' : 'Create client'}
            </Button>
          </form>
        </Modal>

        <Modal
          open={!!inviteTarget}
          onClose={() => setInviteTarget(null)}
          title={inviteTarget ? `Invite portal user · ${inviteTarget.legalName}` : 'Invite portal user'}
        >
          <form onSubmit={sendInvite}>
            <Input
              label="Email"
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              required
            />
            <Input
              label="Temporary password"
              type="text"
              value={inviteForm.password}
              onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
              required
              minLength={10}
              hint="Min 10 characters — user resets on first login"
            />
            <Select
              label="Role"
              value={inviteForm.clientRole}
              onChange={(e) => setInviteForm({ ...inviteForm, clientRole: e.target.value })}
            >
              <option value="Client Main">Client Main — full access + user management</option>
              <option value="Client Sub">Client Sub — book &amp; view own</option>
              <option value="Client Viewer">Client Viewer — view / download only</option>
            </Select>
            <FlashMessage flash={inviteFlash} />
            <Button type="submit" loading={inviteSaving}>Send invite</Button>
          </form>
        </Modal>

        <style>{`
          .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; flex-wrap: wrap; }
          .client-form .grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-md);
          }
          @media (max-width: 640px) {
            .client-form .grid-2 { grid-template-columns: 1fr; }
          }
          .zones-block { margin-bottom: var(--space-md); }
          .zones-block .input-label {
            display: block;
            font-weight: 500;
            margin-bottom: var(--space-xs);
            font-size: 0.875rem;
          }
          .zone-checks {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: var(--space-xs) var(--space-md);
            max-height: 160px;
            overflow: auto;
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-sm) var(--space-md);
          }
          .zone-check {
            display: flex;
            align-items: center;
            gap: var(--space-xs);
            font-size: 0.875rem;
          }
        `}</style>
      </div>
    </Layout>
  );
}
